import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAppAuth } from '../hooks/useAppAuth';
import { createApiClient, invoicesService, jobService } from '../services/api';
import { JobStatus } from '../types';
import type { JobSummaryDto, CreateInvoiceExtraItemInput } from '../types';

const INVOICEABLE_STATUSES = new Set<JobStatus>([
  JobStatus.Completed, JobStatus.PodVerified, JobStatus.PaymentReleased, JobStatus.ReviewUnlocked,
]);

const EXTRA_PRESETS = [
  'Loading/Unloading Charges', 'ULE Charges', 'Congestion Charges', 'Fuel Surcharge', 'Waiting Time',
];

export default function CreateInvoicePage() {
  const { getToken, account } = useAppAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [jobs, setJobs] = useState<JobSummaryDto[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [selectedJobIds, setSelectedJobIds] = useState<Set<string>>(new Set());
  const [isVatRegistered, setIsVatRegistered] = useState(false);
  const [vatRate] = useState(20);
  const [extras, setExtras] = useState<CreateInvoiceExtraItemInput[]>([]);
  const [notes, setNotes] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!account) return;
    (async () => {
      try {
        const api = createApiClient(getToken);
        const [allJobs, settings] = await Promise.all([
          jobService(api).getMyJobs(),
          invoicesService(api).getSettings().catch(() => null),
        ]);
        setJobs(allJobs.filter(j => INVOICEABLE_STATUSES.has(j.status)));
        if (settings) setIsVatRegistered(false);
      } finally {
        setLoadingJobs(false);
      }
    })();
  }, [account, getToken]);

  const toggleJob = (id: string) => {
    setSelectedJobIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const addPresetExtra = (description: string) =>
    setExtras(prev => [...prev, { description, amount: 0, vatRate: isVatRegistered ? vatRate : 0 }]);
  const addCustomExtra = () =>
    setExtras(prev => [...prev, { description: '', amount: 0, vatRate: isVatRegistered ? vatRate : 0 }]);
  const updateExtra = useCallback((idx: number, patch: Partial<CreateInvoiceExtraItemInput>) => {
    setExtras(prev => prev.map((e, i) => (i === idx ? { ...e, ...patch } : e)));
  }, []);
  const removeExtra = (idx: number) => setExtras(prev => prev.filter((_, i) => i !== idx));

  const selectedJobs = jobs.filter(j => selectedJobIds.has(j.id));

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const api = createApiClient(getToken);
      const invoice = await invoicesService(api).create({
        jobIds: Array.from(selectedJobIds),
        extraItems: extras.filter(e => e.description.trim() !== ''),
        notes: notes || undefined,
        dueDate: dueDate || undefined,
        isVatRegistered,
        vatRate,
      });
      navigate(`/invoices/${invoice.id}`);
    } catch (e: any) {
      setError(e.message ?? 'Failed to create invoice.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ width: '100%', padding: '24px 16px' }}>
      <Link to="/invoices" style={{ fontSize: 13, color: '#6b7280', textDecoration: 'none' }}>← Invoices</Link>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: '8px 0 20px' }}>Generate Invoice</h1>

      {step === 0 && (
        <>
          <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>1. Select Jobs</h2>
          {loadingJobs ? (
            <p style={{ color: '#9ca3af' }}>Loading jobs…</p>
          ) : jobs.length === 0 ? (
            <p style={{ color: '#9ca3af' }}>No completed jobs available to invoice yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
              {jobs.map(job => (
                <label key={job.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                  borderRadius: 8, border: '1px solid #e5e7eb', cursor: 'pointer',
                  background: selectedJobIds.has(job.id) ? '#eff6ff' : '#fff',
                }}>
                  <input type="checkbox" checked={selectedJobIds.has(job.id)} onChange={() => toggleJob(job.id)} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{job.referenceNumber} — {job.title}</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>{job.customerName}</div>
                  </div>
                </label>
              ))}
            </div>
          )}
          <button
            onClick={() => setStep(1)}
            disabled={selectedJobIds.size === 0}
            style={{
              fontSize: 13, fontWeight: 600, padding: '10px 16px', borderRadius: 8,
              background: selectedJobIds.size === 0 ? '#e5e7eb' : '#2563eb',
              color: selectedJobIds.size === 0 ? '#9ca3af' : '#fff', border: 'none',
              cursor: selectedJobIds.size === 0 ? 'default' : 'pointer',
            }}
          >
            Next
          </button>
        </>
      )}

      {step === 1 && (
        <>
          <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>2. Add Charges</h2>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, fontSize: 13 }}>
            <input type="checkbox" checked={isVatRegistered} onChange={(e) => setIsVatRegistered(e.target.checked)} />
            VAT registered (adds {vatRate}% VAT to line items)
          </label>

          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
            {EXTRA_PRESETS.map(p => (
              <button key={p} onClick={() => addPresetExtra(p)} style={{
                fontSize: 12, padding: '5px 10px', borderRadius: 20, border: '1px solid #d1d5db',
                background: '#fff', cursor: 'pointer',
              }}>+ {p}</button>
            ))}
            <button onClick={addCustomExtra} style={{
              fontSize: 12, padding: '5px 10px', borderRadius: 20, border: '1px dashed #9ca3af',
              background: '#fff', cursor: 'pointer',
            }}>+ Custom</button>
          </div>

          {extras.map((extra, idx) => (
            <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
              <input
                value={extra.description}
                onChange={(e) => updateExtra(idx, { description: e.target.value })}
                placeholder="Description"
                style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: '1px solid #d1d5db' }}
              />
              <input
                type="number"
                value={extra.amount}
                onChange={(e) => updateExtra(idx, { amount: Number(e.target.value) })}
                placeholder="£"
                style={{ width: 90, padding: '6px 10px', borderRadius: 6, border: '1px solid #d1d5db' }}
              />
              <button onClick={() => removeExtra(idx)} style={{ border: 'none', background: 'none', color: '#dc2626', cursor: 'pointer' }}>✕</button>
            </div>
          ))}

          <div style={{ marginTop: 16 }}>
            <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Notes</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db' }} />
          </div>
          <div style={{ marginTop: 12 }}>
            <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Due Date</label>
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)}
              style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db' }} />
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
            <button onClick={() => setStep(0)} style={{
              fontSize: 13, fontWeight: 600, padding: '10px 16px', borderRadius: 8,
              border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer',
            }}>Back</button>
            <button onClick={() => setStep(2)} style={{
              fontSize: 13, fontWeight: 600, padding: '10px 16px', borderRadius: 8,
              background: '#2563eb', color: '#fff', border: 'none', cursor: 'pointer',
            }}>Preview</button>
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>3. Preview</h2>
          <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, marginBottom: 16 }}>
            {selectedJobs.map(job => (
              <div key={job.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
                <span>{job.referenceNumber} — {job.title}</span>
              </div>
            ))}
            {extras.filter(e => e.description).map((e, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
                <span>{e.description}</span>
                <span>£{e.amount.toFixed(2)}</span>
              </div>
            ))}
            {notes && <p style={{ fontSize: 12, color: '#6b7280', marginTop: 10 }}>{notes}</p>}
          </div>

          <div style={{
            background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10,
            padding: '10px 14px', marginBottom: 16, fontSize: 12, color: '#92400e',
          }}>
            Approving will generate a PDF and send this invoice to the customer by email. This action cannot be undone.
          </div>

          {error && <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{error}</p>}

          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setStep(1)} style={{
              fontSize: 13, fontWeight: 600, padding: '10px 16px', borderRadius: 8,
              border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer',
            }}>Back</button>
            <button onClick={handleSubmit} disabled={submitting} style={{
              fontSize: 13, fontWeight: 600, padding: '10px 16px', borderRadius: 8,
              background: submitting ? '#e5e7eb' : '#2563eb', color: submitting ? '#9ca3af' : '#fff',
              border: 'none', cursor: submitting ? 'default' : 'pointer',
            }}>{submitting ? 'Creating…' : 'Create Invoice (Draft)'}</button>
          </div>
        </>
      )}
    </div>
  );
}
